import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Trash2, ArrowLeft, Loader2,
  XCircle, MessageSquare, Megaphone,
  Image as ImageIcon, FileText, Video,
  MoreVertical, ChevronDown, Check, Info, Bell, ShoppingBag, PhoneCall
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: "MARKETING", label: "Marketing", icon: Megaphone, desc: "Promotions, launches, re-engagement" },
  { value: "UTILITY", label: "Utility", icon: Bell, desc: "Order updates, transaction alerts, billing" }
];

const COUNTRY_CODES = [
  { value: "+1", label: "US +1" },
  { value: "+91", label: "IN +91" },
  { value: "+44", label: "GB +44" },
  { value: "+971", label: "AE +971" },
  { value: "+65", label: "SG +65" },
  { value: "+60", label: "MY +60" },
  { value: "+61", label: "AU +61" },
  { value: "+81", label: "JP +81" },
  { value: "+49", label: "DE +49" },
  { value: "+33", label: "FR +33" },
  { value: "+86", label: "CN +86" },
  { value: "+966", label: "SA +966" },
  { value: "+974", label: "QA +974" },
  { value: "+973", label: "BH +973" },
  { value: "+965", label: "KW +965" },
  { value: "+968", label: "OM +968" },
  { value: "+20", label: "EG +20" },
  { value: "+7", label: "RU +7" },
  { value: "+39", label: "IT +39" },
  { value: "+34", label: "ES +34" },
  { value: "+55", label: "BR +55" },
  { value: "+52", label: "MX +52" },
  { value: "+27", label: "ZA +27" }
];

const LANGUAGES = [
  { value: "af", label: "Afrikaans (af)" },
  { value: "sq", label: "Albanian (sq)" },
  { value: "ar", label: "Arabic (ar)" },
  { value: "az", label: "Azerbaijani (az)" },
  { value: "bn", label: "Bengali (bn)" },
  { value: "bg", label: "Bulgarian (bg)" },
  { value: "ca", label: "Catalan (ca)" },
  { value: "zh_CN", label: "Chinese (CHN)" },
  { value: "zh_HK", label: "Chinese (HKG)" },
  { value: "zh_TW", label: "Chinese (TAI)" },
  { value: "hr", label: "Croatian (hr)" },
  { value: "cs", label: "Czech (cs)" },
  { value: "da", label: "Danish (da)" },
  { value: "nl", label: "Dutch (nl)" },
  { value: "en", label: "English (en)" },
  { value: "en_GB", label: "English (UK)" },
  { value: "en_US", label: "English (US)" },
  { value: "et", label: "Estonian (et)" },
  { value: "fil", label: "Filipino (fil)" },
  { value: "fi", label: "Finnish (fi)" },
  { value: "fr", label: "French (fr)" },
  { value: "de", label: "German (de)" },
  { value: "el", label: "Greek (el)" },
  { value: "gu", label: "Gujarati (gu)" },
  { value: "ha", label: "Hausa (ha)" },
  { value: "he", label: "Hebrew (he)" },
  { value: "hi", label: "Hindi (hi)" },
  { value: "hu", label: "Hungarian (hu)" },
  { value: "id", label: "Indonesian (id)" },
  { value: "ga", label: "Irish (ga)" },
  { value: "it", label: "Italian (it)" },
  { value: "ja", label: "Japanese (ja)" },
  { value: "kn", label: "Kannada (kn)" },
  { value: "kk", label: "Kazakh (kk)" },
  { value: "ko", label: "Korean (ko)" },
  { value: "lo", label: "Lao (lo)" },
  { value: "lv", label: "Latvian (lv)" },
  { value: "lt", label: "Lithuanian (lt)" },
  { value: "mk", label: "Macedonian (mk)" },
  { value: "ms", label: "Malay (ms)" },
  { value: "ml", label: "Malayalam (ml)" },
  { value: "mr", label: "Marathi (mr)" },
  { value: "nb", label: "Norwegian (nb)" },
  { value: "fa", label: "Persian (fa)" },
  { value: "pl", label: "Polish (pl)" },
  { value: "pt_BR", label: "Portuguese (BR) (pt_BR)" },
  { value: "pt_PT", label: "Portuguese (PT) (pt_PT)" },
  { value: "pa", label: "Punjabi (pa)" },
  { value: "ro", label: "Romanian (ro)" },
  { value: "ru", label: "Russian (ru)" },
  { value: "sr", label: "Serbian (sr)" },
  { value: "sk", label: "Slovak (sk)" },
  { value: "sl", label: "Slovenian (sl)" },
  { value: "es", label: "Spanish (es)" },
  { value: "es_AR", label: "Spanish (Argentina) (es_AR)" },
  { value: "es_MX", label: "Spanish (Mexico) (es_MX)" },
  { value: "es_ES", label: "Spanish (Spain) (es_ES)" },
  { value: "sw", label: "Swahili (sw)" },
  { value: "sv", label: "Swedish (sv)" },
  { value: "ta", label: "Tamil (ta)" },
  { value: "te", label: "Telugu (te)" },
  { value: "th", label: "Thai (th)" },
  { value: "tr", label: "Turkish (tr)" },
  { value: "uk", label: "Ukrainian (uk)" },
  { value: "ur", label: "Urdu (ur)" },
  { value: "uz", label: "Uzbek (uz)" },
  { value: "vi", label: "Vietnamese (vi)" },
  { value: "zu", label: "Zulu (zu)" }
];

const STATUS_CONFIG = {
  APPROVED: { label: "Approved", color: "#10b981", className: "bg-emerald-50 text-emerald-700 border-emerald-200/60" },
  PENDING: { label: "Pending", color: "#f59e0b", className: "bg-amber-50 text-amber-700 border-amber-200/60" },
  IN_APPEAL: { label: "In Appeal", color: "#8b5cf6", className: "bg-purple-50 text-purple-700 border-purple-200/60" },
  REJECTED: { label: "Rejected", color: "#ef4444", className: "bg-red-50 text-red-700 border-red-200/60" },
  PAUSED: { label: "Paused", color: "#6b7280", className: "bg-zinc-100 text-zinc-600 border-zinc-200" },
  DISABLED: { label: "Disabled", color: "#ef4444", className: "bg-red-50 text-red-700 border-red-200/60" },
};

const emptyForm = { name: "", category: "MARKETING", language: "en", headerType: "NONE", headerText: "", body: "", footer: "", buttons: [], sampleBody: [], sampleHeader: "" };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseExistingTemplate = (t) => {
  const form = { ...emptyForm, name: t.name, category: t.category, language: t.language };
  (t.components || []).forEach(c => {
    if (c.type === "HEADER") { form.headerType = c.format || "TEXT"; form.headerText = c.text || ""; form.sampleHeader = c.example?.header_text?.[0] || ""; }
    if (c.type === "BODY") { form.body = c.text || ""; form.sampleBody = c.example?.body_text?.[0] || []; }
    if (c.type === "FOOTER") form.footer = c.text || "";
    if (c.type === "BUTTONS") {
      const btns = c.buttons || [];
      const first = btns[0];
      if (first?.type === "QUICK_REPLY") {
        form.buttonType = "QUICK_REPLIES";
        form.buttons = btns.map(b => ({ type: "QUICK_REPLY", text: b.text, payload: b.payload || "" }));
      } else if (first?.type === "COPY_CODE") {
        form.buttonType = "COPY_CODE";
        form.copyCodeButton = first.example?.[0] || "";
      } else if (first?.type === "CATALOG") {
        form.buttonType = "CATALOG";
      } else {
        form.buttonType = "URL";
        const urlBtn = btns.find(b => b.type === "URL");
        const phoneBtn = btns.find(b => b.type === "PHONE_NUMBER");
        form.urlButton = { text: urlBtn?.text || "", url: urlBtn?.url || "" };
        form.phoneButton = { text: phoneBtn?.text || "", phone: phoneBtn?.phone_number || "" };
      }
    }
  });
  return form;
};

// ─── Custom Dropdown UI ──────────────────────────────────────────────────────

const CustomDropdown = ({ label, options = [], value, onChange, disabled, size = "large" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const portalId = useRef(`wa-tpl-dd-${Math.random().toString(36).substring(2, 9)}`);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, openUp: false, maxHeight: 240 });

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  const updatePosition = useCallback(() => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedHeight = Math.min(options.length * 36 + 12, 240);

    const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(openUp ? spaceAbove - 16 : spaceBelow - 16, 240));

    setCoords({
      top: openUp ? rect.top + window.scrollY : rect.bottom + window.scrollY + 6,
      left: rect.left + window.scrollX,
      width: rect.width,
      openUp,
      maxHeight,
    });
  }, [options.length]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        const portalEl = document.getElementById(portalId.current);
        if (portalEl && portalEl.contains(e.target)) return;
        setIsOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      updatePosition();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  const isSmall = size === "small";

  return (
    <div ref={dropdownRef} className="relative w-full" style={{ opacity: disabled ? 0.65 : 1 }}>
      {label && <label className="wa-label" style={isSmall ? { fontSize: "11px", fontWeight: "700" } : {}}>{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) updatePosition();
            setIsOpen(!isOpen);
          }
        }}
        className={`w-full bg-white border text-left text-xs font-medium rounded-xl transition-all flex items-center justify-between outline-none ${
          isSmall ? "px-3 py-2" : "px-3.5 py-2.5"
        } ${
          disabled
            ? "opacity-40 cursor-not-allowed bg-zinc-50/50 border-zinc-200"
            : isOpen
            ? "border-zinc-900 shadow-sm ring-1 ring-zinc-900/5 bg-white"
            : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/40 text-zinc-900 cursor-pointer"
        }`}
      >
        <span className={`truncate ${selectedOption ? "text-zinc-900 font-medium" : "text-zinc-400"}`}>
          {selectedOption ? selectedOption.label : "Select option"}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform duration-200 text-zinc-400 ml-2 ${
            isOpen ? "rotate-180 text-zinc-900" : ""
          }`}
        />
      </button>

      {isOpen &&
        !disabled &&
        createPortal(
          <div
            id={portalId.current}
            style={{
              position: "absolute",
              top: coords.openUp ? "auto" : `${coords.top}px`,
              bottom: coords.openUp
                ? `${document.documentElement.scrollHeight - coords.top + 6}px`
                : "auto",
              left: `${coords.left}px`,
              width: `${coords.width}px`,
              maxHeight: `${coords.maxHeight}px`,
              zIndex: 99999,
            }}
            className="py-1 bg-white border border-zinc-200/90 rounded-xl overflow-y-auto shadow-xl shadow-zinc-950/10 custom-scrollbar animate-fadeIn"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3.5 py-2 text-xs text-left flex items-center justify-between transition-colors ${
                    isSelected
                      ? "bg-zinc-100 text-zinc-900 font-semibold"
                      : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                  }`}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSelected && <Check size={13} className="text-zinc-900 shrink-0 ml-2" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

// ─── Catalog Connection Modal ──────────────────────────────────────────────────

const CatalogModal = ({ onClose, onConnect, isCatalogConnected, catalogSearch, setCatalogSearch, showToast }) => {
  const [catalogList, setCatalogList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);

  const handleSearch = async () => {
    // Avoid redundant calls if already loading
    if (loading) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await ApiClient.request("/whatsapp/catalogs");
      if (res && res.catalogs) {
        setCatalogList(res.catalogs);
      } else {
        setCatalogList([]);
        showToast("No catalogues found for your business account.", "error");
      }
    } catch (e) {
      setCatalogList([]);
      showToast("Failed to fetch catalogues. Check your WhatsApp configuration.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedCatalog) return;
    setConnecting(true);
    try {
      const res = await ApiClient.request("/whatsapp/catalogs/connect", {
        method: "POST",
        body: JSON.stringify({ catalog_id: selectedCatalog.id })
      });
      if (res && res.success) {
        onConnect(selectedCatalog);
      } else {
        const errMsg = res?.detail || "Failed to connect catalogue to WABA.";
        showToast(errMsg, "error");
      }
    } catch (e) {
      const errMsg = e?.detail || e?.message || "Error connecting catalogue to WABA.";
      showToast(errMsg, "error");
    } finally {
      setConnecting(false);
    }
  };

  const filtered = catalogList.filter(item =>
    (item.name || "").toLowerCase().includes((catalogSearch || "").toLowerCase()) ||
    (item.id || "").includes(catalogSearch || "")
  );

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(15, 23, 42, 0.6)", display: "flex",
      alignItems: "center", justifyContent: "center",
      zIndex: 200, backdropFilter: "blur(4px)"
    }}>
      <div style={{
        background: "#ffffff", borderRadius: "16px", width: "540px",
        padding: "24px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
        display: "flex", flexDirection: "column", gap: "18px"
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "18px", fontWeight: 800, color: "#1e293b" }}>Connect a catalogue</span>
          <button type="button" onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "20px", color: "#64748b", cursor: "pointer" }}>✕</button>
        </div>

        {/* Description */}
        <p style={{ fontSize: "13px", color: "#475569", lineHeight: "1.5", margin: 0 }}>
          Connecting a catalogue will allow customers to view, message and send carts containing your products and services via WhatsApp.{" "}
          <button type="button" onClick={() => {}} style={{ background: "none", border: "none", color: "#18181b", fontWeight: 600, cursor: "pointer", padding: 0, fontSize: "inherit" }}>Learn more</button>
        </p>

        {/* Search Row */}
        <div style={{ display: "flex", gap: "8px" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Search size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              placeholder="Search by catalogue name or ID"
              value={catalogSearch}
              onChange={e => setCatalogSearch(e.target.value)}
              onFocus={handleSearch}
              onClick={handleSearch}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              style={{
                width: "100%", padding: "11px 14px 11px 42px", borderRadius: "10px",
                border: "1px solid #e4e4e7", fontSize: "13px",
                outline: "none", boxSizing: "border-box", fontWeight: 500
              }}
            />
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={loading}
            style={{
              padding: "11px 18px", borderRadius: "10px", background: "#18181b",
              color: "#fff", border: "none", fontWeight: 700, fontSize: "13px",
              cursor: loading ? "not-allowed" : "pointer", whiteSpace: "nowrap",
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {/* Results */}
        {hasSearched && (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden", background: "#fff", maxHeight: "220px", overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>Fetching catalogues from Meta...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>No catalogues found.</div>
            ) : (
              filtered.map(item => {
                const isSelected = selectedCatalog?.id === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedCatalog(item)}
                    style={{
                      padding: "12px 16px", cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9", display: "flex",
                      justifyContent: "space-between", alignItems: "center",
                      background: isSelected ? "#f4f4f5" : "none",
                      transition: "background 0.1s"
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "none"; }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>{item.name}</span>
                      <span style={{ fontSize: "11px", color: "#64748b" }}>{item.id}{item.product_count != null ? ` · ${item.product_count} products` : ""}</span>
                    </div>
                    {isSelected && <span style={{ color: "#18181b", fontSize: "18px", fontWeight: 800 }}>✓</span>}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
          <button type="button" onClick={onClose}
            style={{ padding: "10px 20px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#ffffff", color: "#475569", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedCatalog || connecting}
            onClick={handleConnect}
            style={{
              padding: "10px 20px", border: "none", borderRadius: "8px",
              background: selectedCatalog ? "#18181b" : "#e4e4e7",
              color: "#ffffff", fontWeight: 700, fontSize: "13px",
              cursor: selectedCatalog && !connecting ? "pointer" : "not-allowed",
              opacity: connecting ? 0.7 : 1
            }}
          >
            {connecting ? "Connecting..." : "Connect Catalogue"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Template Form with Preview ──────────────────────────────────────────

const TemplateForm = ({ initial, templateId, onSave, onCancel, onDelete, isEdit, rejectedReason, status }) => {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form State
  const [name, setName] = useState(initial?.name || "");
  const [category, setCategory] = useState(initial?.category || "MARKETING");
  const [templateType, setTemplateType] = useState(
    initial?.buttonType === "CATALOG" ? "CATALOG" : "DEFAULT"
  );
  const [language, setLanguage] = useState(initial?.language || "en");
  const [headerType, setHeaderType] = useState(initial?.headerType || "NONE");
  const [headerText, setHeaderText] = useState(initial?.headerText || "");
  const [sampleHeader, setSampleHeader] = useState(initial?.sampleHeader || "");
  const [sampleMediaUrl, setSampleMediaUrl] = useState(initial?.sampleMediaUrl || "");
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [body, setBody] = useState(initial?.body || "");
  const [sampleBody, setSampleBody] = useState(initial?.sampleBody || []);
  const [footer, setFooter] = useState(initial?.footer || "");
  const [templateButtons, setTemplateButtons] = useState(() => {
    if (initial?.buttons) {
      return initial.buttons.map(b => {
        if (b.type === "QUICK_REPLY") {
          return { id: Math.random(), type: "QUICK_REPLY", text: b.text, payload: b.payload || "" };
        }
        if (b.type === "URL") {
          return { id: Math.random(), type: "URL", text: b.text, url: b.url, urlType: b.url?.includes("{{1}}") ? "DYNAMIC" : "STATIC", example: b.example?.[0] || "" };
        }
        if (b.type === "COPY_CODE") {
          return { id: Math.random(), type: "COPY_CODE", code: b.code || b.example?.[0] || "" };
        }
        if (b.type === "PHONE_NUMBER") {
          const match = (b.phone_number || "").match(/^(\+\d+)(.*)$/);
          return { id: Math.random(), type: "PHONE_NUMBER", text: b.text, countryCode: match ? match[1] : "+1", phone: match ? match[2] : "" };
        }
        return { id: Math.random(), ...b };
      });
    }
    return [];
  });
  const [catalogFormat, setCatalogFormat] = useState(
    initial?.buttons?.find(b => b.type === "CATALOG")?.text === "View items" ? "MULTI_PRODUCT" : "CATALOG"
  );
  const [showAddButtonDropdown, setShowAddButtonDropdown] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [isCatalogConnected, setIsCatalogConnected] = useState(initial?.catalogConnected || false);

  const [copyCodeButton, setCopyCodeButton] = useState(
    initial?.buttons?.find(b => b.type === "COPY_CODE")?.code || initial?.copyCodeButton || ""
  );
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(
    initial?.addSecurityRecommendation !== undefined ? initial.addSecurityRecommendation : true
  );
  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState(
    initial?.codeExpirationMinutes || 10
  );

  // Sync templateType & buttonType on category change
  useEffect(() => {
    if (category === "AUTHENTICATION") {
      setTemplateType("OTP");
      setTemplateButtons([{ id: Math.random(), type: "COPY_CODE", code: "" }]);
      setHeaderType("NONE");
    } else {
      if (templateType === "OTP") {
        setTemplateType("DEFAULT");
        setTemplateButtons([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Sync configurations depending on subtype selected
  useEffect(() => {
    if (templateType === "CATALOG") {
      setTemplateButtons([]);
    } else if (templateType === "CALL_PERMISSION") {
      setHeaderType("NONE");
      setFooter("");
      setTemplateButtons([]);
    } else if (templateType === "OTP") {
      setHeaderType("NONE");
      setTemplateButtons([{ id: Math.random(), type: "COPY_CODE", code: "" }]);
    }
  }, [templateType]);

  const bodyVars = [...new Set([...body.matchAll(/\{\{(\d+)\}\}/g)].map(m => parseInt(m[1])))].sort((a, b) => a - b);
  const headerVars = [...new Set([...headerText.matchAll(/\{\{(\d+)\}\}/g)].map(m => parseInt(m[1])))].sort((a, b) => a - b);

  const addVariable = () => {
    const nextVar = bodyVars.length + 1;
    setBody(prev => prev + ` {{${nextVar}}}`);
  };

  const getPreviewText = () => {
    let text = body;
    bodyVars.forEach(v => {
      const val = sampleBody[v - 1] || `[Variable ${v}]`;
      text = text.replace(`{{${v}}}`, val);
    });
    return text;
  };

  const getPreviewHeader = () => {
    if (headerType !== "TEXT") return null;
    let text = headerText;
    headerVars.forEach(v => {
      const val = sampleHeader || `[Header Var]`;
      text = text.replace(`{{${v}}}`, val);
    });
    return text;
  };

  const getPayloadButtons = () => {
    if (templateType === "OTP") {
      return [{ type: "OTP", otp_type: "COPY_CODE", text: copyCodeButton || "Copy Code" }];
    }
    if (templateType === "CATALOG") {
      const btnText = catalogFormat === "MULTI_PRODUCT" ? "View items" : "View catalog";
      return [{ type: "CATALOG", text: btnText }];
    }
    if (templateType === "CALL_PERMISSION") {
      return [];
    }
    return templateButtons.map(btn => {
      if (btn.type === "QUICK_REPLY") {
        const res = { type: "QUICK_REPLY", text: btn.text || "Quick reply" };
        if (btn.payload) {
          res.payload = btn.payload;
        }
        return res;
      }
      if (btn.type === "URL") {
        const isDynamic = btn.urlType === "DYNAMIC" || (btn.url || "").includes("{{1}}");
        const res = { type: "URL", text: btn.text || "Visit website", url: btn.url || "https://example.com" };
        if (isDynamic) {
          res.example = [btn.example || "ORD12345"];
        }
        return res;
      }
      if (btn.type === "COPY_CODE") {
        return { type: "COPY_CODE", example: [btn.code || "NEWYEAR20"] };
      }
      if (btn.type === "PHONE_NUMBER") {
        const prefix = (btn.countryCode || "+1").startsWith("+") ? (btn.countryCode || "+1") : "+" + (btn.countryCode || "+1");
        const fullPhone = prefix + (btn.phone || "");
        return { type: "PHONE_NUMBER", text: btn.text || "Call Us", phone_number: fullPhone };
      }
      return btn;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) { showToast("Template name is required", "error"); return; }
    if (!/^[a-z0-9_]+$/.test(name)) { showToast("Template name must be lowercase, numbers, underscores only", "error"); return; }

    // Validation for phone buttons
    const phoneBtn = templateButtons.find(b => b.type === "PHONE_NUMBER");
    if (phoneBtn && !(phoneBtn.phone || "").trim()) {
      showToast("Phone number is required for the Call Phone Number button.", "error");
      return;
    }

    // Body validation only required for non-OTP templates
    if (templateType !== "OTP" && !body.trim()) {
      showToast("Body text is required", "error");
      return;
    }

    const components = [];

    if (templateType === "OTP" || category === "AUTHENTICATION") {
      components.push({ type: "BODY", add_security_recommendation: addSecurityRecommendation });
      components.push({ type: "FOOTER", code_expiration_minutes: parseInt(codeExpirationMinutes) || 10 });
      const buttons = getPayloadButtons();
      if (buttons.length > 0) {
        components.push({ type: "BUTTONS", buttons });
      }
    } else {
      // Header component
      if (templateType !== "CALL_PERMISSION" && headerType !== "NONE") {
        if (headerType === "TEXT") {
          const comp = { type: "HEADER", format: "TEXT", text: headerText };
          if (sampleHeader) comp.example = { header_text: [sampleHeader] };
          components.push(comp);
        } else {
          const comp = { type: "HEADER", format: headerType };
          if (sampleMediaUrl) {
            comp.sample_media_url = sampleMediaUrl;
          }
          components.push(comp);
        }
      }

      // Body component
      const bodyComp = { type: "BODY", text: body };
      const filteredSampleBody = sampleBody.slice(0, bodyVars.length);
      if (filteredSampleBody.length > 0) {
        bodyComp.example = { body_text: [filteredSampleBody] };
      }
      components.push(bodyComp);

      // Footer component
      if (templateType !== "CALL_PERMISSION" && footer.trim()) {
        components.push({ type: "FOOTER", text: footer });
      }

      // Interactive template specific components or standard buttons
      if (templateType === "CALL_PERMISSION") {
        components.push({ type: "CALL_PERMISSION_REQUEST" });
      } else {
        const buttons = getPayloadButtons();
        if (buttons.length > 0) {
          components.push({
            type: "BUTTONS",
            buttons: buttons.map(b => {
              if (b.type === "QUICK_REPLY") return { type: "QUICK_REPLY", text: b.text };
              if (b.type === "URL") return { type: "URL", text: b.text, url: b.url, ...(b.example && { example: b.example }) };
              if (b.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone_number };
              if (b.type === "COPY_CODE") return { type: "COPY_CODE", example: b.example };
              if (b.type === "CATALOG") return { type: "CATALOG", text: "View catalog" };
              return b;
            })
          });
        }
      }
    }

    const payload = isEdit
      ? { category, components }
      : { name, category, language, components };

    setSaving(true);
    try {
      if (isEdit) {
        await ApiClient.request(`/whatsapp/templates/${templateId}`, { method: "PUT", body: JSON.stringify(payload) });
        showToast("Template updated successfully!", "success");
      } else {
        await ApiClient.request("/whatsapp/templates", { method: "POST", body: JSON.stringify(payload) });
        showToast("Template submitted successfully!", "success");
      }
      onSave();
    } catch (e) {
      // Handled globally
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await ApiClient.request(`/whatsapp/templates?name=${encodeURIComponent(name)}`, { method: "DELETE" });
      showToast("Template deleted successfully!", "success");
      onDelete();
    } catch (e) { }
  };



  return (
    <div style={{ display: "flex", gap: "32px", width: "100%", alignItems: "flex-start" }}>
      {/* Left Form Panel */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "24px" }}>
        {status === "REJECTED" && (
          <div style={{
            background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: "16px",
            padding: "16px 20px", display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "8px"
          }}>
            <XCircle size={20} style={{ color: "#ef4444", flexShrink: 0, marginTop: "2px" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <span style={{ fontSize: "14px", fontWeight: 800, color: "#991b1b" }}>This template was rejected by Meta</span>
              <span style={{ fontSize: "13px", color: "#7f1d1d", fontWeight: 500, lineHeight: 1.4 }}>
                {rejectedReason || "No rejection reason provided by Meta. Check your category and content compliance."}
              </span>
            </div>
          </div>
        )}

        {/* Name Input */}
        <div>
          <label className="wa-label">Template Name</label>
          <input
            className="wa-input"
            placeholder="e.g. order_update"
            value={name}
            disabled={isEdit}
            onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            style={{ fontWeight: 500 }}
          />
        </div>

        {/* Category & Template Type selection using CustomDropdowns */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <CustomDropdown
            label="Category"
            options={CATEGORIES.map(c => ({ value: c.value, label: c.label }))}
            value={category}
            onChange={setCategory}
            disabled={isEdit}
          />
          <CustomDropdown
            label="Template Type"
            options={(() => {
              if (category === "MARKETING") {
                return [
                  { value: "DEFAULT", label: "Default" },
                  { value: "CATALOG", label: "Catalogue" }
                ];
              }
              if (category === "UTILITY") {
                return [
                  { value: "DEFAULT", label: "Default" },
                  { value: "CATALOG", label: "Catalogue" }
                ];
              }
              return [];
            })()}
            value={templateType}
            onChange={setTemplateType}
            disabled={isEdit}
          />
        </div>

        {/* Guidelines Helper Box */}
        <div style={{
          background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px",
          padding: "12px 16px", display: "flex", gap: "8px", alignItems: "flex-start", marginTop: "-8px"
        }}>
          <Info size={16} className="text-zinc-500 shrink-0 mt-0.5" />
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#475569" }}>
              {category === "MARKETING" ? "Marketing Category Guidelines" : category === "UTILITY" ? "Utility Category Guidelines" : "Authentication Category Guidelines"}
            </span>
            <span style={{ fontSize: "11.5px", color: "#64748b", lineHeight: 1.45 }}>
              {category === "MARKETING" && "Use for promotions, offers, calls-to-action, catalogues, and agreements to voice call permissions. If you mix promotional message text with transaction details, it MUST be Marketing."}
              {category === "UTILITY" && "Use strictly for transactional updates (order notifications, delivery statuses, billing alerts). Absolutely NO promotional language, discounts, or marketing hooks are allowed (Meta will reject immediately)."}
              {category === "AUTHENTICATION" && "Strictly restricted to verification codes, logins, or One-Time Passcodes (OTPs)."}
            </span>
          </div>
        </div>

        {/* Buttons type dropdown - show only if default subtype selected */}
        {/* Buttons list and selector - show only if default subtype selected */}
        {templateType === "DEFAULT" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {templateButtons.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {templateButtons.map((btn, index) => {
                  return (
                    <div key={btn.id || index} className="wa-section" style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                      
                      {/* Button Card Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                        <span style={{ fontSize: "12px", fontWeight: 800, color: "#475569" }}>
                          Button #{index + 1}: {btn.type === "URL" ? "Visit Website" : btn.type === "QUICK_REPLY" ? "Quick Reply" : btn.type === "PHONE_NUMBER" ? "Call Phone Number" : "Copy Code"}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setTemplateButtons(templateButtons.filter((_, i) => i !== index));
                          }}
                          style={{ background: "none", border: "none", color: "#ef4444", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
                        >
                          Remove
                        </button>
                      </div>

                      {/* Config Fields based on button type */}
                      {btn.type === "URL" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <div style={{ display: "flex", alignItems: "flex-end", gap: "12px" }}>
                            
                            {/* Type of action */}
                            <div style={{ flex: 1 }}>
                              <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Type of action</label>
                              <select className="wa-input" value="URL" disabled style={{ background: "#f1f5f9", cursor: "not-allowed", fontSize: "12px" }}>
                                <option value="URL">Visit website</option>
                              </select>
                            </div>

                            {/* Button text */}
                            <div style={{ flex: 1.5, position: "relative" }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Button text</label>
                                <span style={{ fontSize: "10px", color: "#94a3b8" }}>{(btn.text || "").length}/40</span>
                              </div>
                              <input
                                className="wa-input"
                                placeholder="e.g. Visit website"
                                maxLength={40}
                                value={btn.text || ""}
                                onChange={e => {
                                  const updated = [...templateButtons];
                                  updated[index].text = e.target.value;
                                  setTemplateButtons(updated);
                                }}
                                style={{ fontSize: "12px" }}
                              />
                            </div>

                            {/* URL type */}
                            <div style={{ flex: 1 }}>
                              <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>URL type</label>
                              <select
                                className="wa-input"
                                value={btn.urlType || "STATIC"}
                                onChange={e => {
                                  const newType = e.target.value;
                                  const updated = [...templateButtons];
                                  updated[index].urlType = newType;
                                  let currentUrl = btn.url || "";
                                  if (newType === "DYNAMIC" && !currentUrl.includes("{{1}}")) {
                                    const base = currentUrl.endsWith("/") ? currentUrl : currentUrl + "/";
                                    updated[index].url = base + "{{1}}";
                                  } else if (newType === "STATIC" && currentUrl.includes("{{1}}")) {
                                    updated[index].url = currentUrl.replace("{{1}}", "").replace(/\/$/, "");
                                  }
                                  setTemplateButtons(updated);
                                }}
                                style={{ fontSize: "12px" }}
                              >
                                <option value="STATIC">Static</option>
                                <option value="DYNAMIC">Dynamic</option>
                              </select>
                            </div>

                            {/* Website URL */}
                            <div style={{ flex: 2.5 }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Website URL</label>
                                <span style={{ fontSize: "10px", color: "#94a3b8" }}>{(btn.url || "").length}/2000</span>
                              </div>
                              <input
                                className="wa-input"
                                placeholder="https://www.yourwebsite.com"
                                maxLength={2000}
                                value={btn.url || ""}
                                onChange={e => {
                                  const updated = [...templateButtons];
                                  updated[index].url = e.target.value;
                                  setTemplateButtons(updated);
                                }}
                                style={{ fontSize: "12px" }}
                              />
                            </div>

                          </div>

                          {(btn.urlType === "DYNAMIC" || (btn.url || "").includes("{{1}}")) && (
                            <div style={{ marginTop: "6px", background: "#ffffff", padding: "12px", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                              <label className="wa-label" style={{ fontSize: "11px", margin: 0 }}>Dynamic URL Example Value (required by Meta review):</label>
                              <input
                                className="wa-input"
                                placeholder="e.g. ORD12345"
                                value={btn.example || ""}
                                onChange={e => {
                                  const updated = [...templateButtons];
                                  updated[index].example = e.target.value;
                                  setTemplateButtons(updated);
                                }}
                                style={{ marginTop: "6px", fontSize: "12px" }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {btn.type === "QUICK_REPLY" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Button text</label>
                            <span style={{ fontSize: "10px", color: "#94a3b8" }}>{(btn.text || "").length}/25</span>
                          </div>
                          <input
                            className="wa-input"
                            placeholder="e.g. Yes, sign me up!"
                            maxLength={25}
                            value={btn.text || ""}
                            onChange={e => {
                              const updated = [...templateButtons];
                              updated[index].text = e.target.value;
                              setTemplateButtons(updated);
                            }}
                            style={{ fontSize: "12px" }}
                          />
                        </div>
                      )}

                      {btn.type === "COPY_CODE" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Copy Code Example Value (required by Meta)</label>
                          <input
                            className="wa-input"
                            placeholder="Sample coupon/verification code (e.g. NEWYEAR20)"
                            value={btn.code || ""}
                            onChange={e => {
                              const updated = [...templateButtons];
                              updated[index].code = e.target.value;
                              setTemplateButtons(updated);
                            }}
                            style={{ fontSize: "12px" }}
                          />
                        </div>
                      )}

                      {btn.type === "PHONE_NUMBER" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <div style={{ display: "flex", alignItems: "flex-end", gap: "12px" }}>
                            {/* Type of action */}
                            <div style={{ flex: 1.2 }}>
                              <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Type of action</label>
                              <select className="wa-input" value="PHONE_NUMBER" disabled style={{ background: "#f1f5f9", cursor: "not-allowed", fontSize: "12px" }}>
                                <option value="PHONE_NUMBER">Call phone number</option>
                              </select>
                            </div>

                            {/* Button text */}
                            <div style={{ flex: 1.5, position: "relative" }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Button text</label>
                                <span style={{ fontSize: "10px", color: "#94a3b8" }}>{(btn.text || "").length}/25</span>
                              </div>
                              <input
                                className="wa-input"
                                placeholder="e.g. Call phone number"
                                maxLength={25}
                                value={btn.text || ""}
                                onChange={e => {
                                  const updated = [...templateButtons];
                                  updated[index].text = e.target.value;
                                  setTemplateButtons(updated);
                                }}
                                style={{ fontSize: "12px" }}
                              />
                            </div>

                            {/* Country */}
                            <div style={{ flex: 1.2 }}>
                              <CustomDropdown
                                label="Country"
                                options={COUNTRY_CODES}
                                value={btn.countryCode || "+1"}
                                onChange={val => {
                                  const updated = [...templateButtons];
                                  updated[index].countryCode = val;
                                  setTemplateButtons(updated);
                                }}
                                size="small"
                              />
                            </div>

                            {/* Phone number */}
                            <div style={{ flex: 2, position: "relative" }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700" }}>Phone number</label>
                                <span style={{ fontSize: "10px", color: "#94a3b8" }}>{(btn.phone || "").length}/20</span>
                              </div>
                              <input
                                className="wa-input"
                                placeholder="e.g. 9876543210"
                                maxLength={20}
                                value={btn.phone || ""}
                                onChange={e => {
                                  const val = e.target.value.replace(/\D/g, ""); // digits only
                                  const updated = [...templateButtons];
                                  updated[index].phone = val;
                                  setTemplateButtons(updated);
                                }}
                                style={{ fontSize: "12px" }}
                              />
                            </div>
                          </div>

                          {!(btn.phone || "").trim() && (
                            <div style={{ color: "#ef4444", fontSize: "11px", fontWeight: 600, marginTop: "2.5px" }}>
                              You need to enter a phone number. Please add a valid phone number.
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
            )}

            {/* "+ Add Button" Widget */}
            {templateButtons.length < 10 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => setShowAddButtonDropdown(!showAddButtonDropdown)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      borderRadius: "10px",
                      border: "1.5px dashed #cbd5e1",
                      background: "#ffffff",
                      color: "#0f172a",
                      fontWeight: 700,
                      fontSize: "13px",
                      cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                  >
                    <span>+ Add Button</span>
                  </button>

                  {showAddButtonDropdown && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      marginTop: "6px",
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      zIndex: 50,
                      width: "180px",
                      display: "flex",
                      flexDirection: "column",
                      padding: "4px"
                    }}>
                      {templateButtons.filter(b => b.type === "URL").length < 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            setTemplateButtons([...templateButtons, { id: Date.now() + Math.random(), type: "URL", text: "", url: "", urlType: "STATIC", example: "" }]);
                            setShowAddButtonDropdown(false);
                          }}
                          style={{ padding: "8px 12px", textAlign: "left", fontSize: "12px", fontWeight: 600, background: "none", border: "none", cursor: "pointer", borderRadius: "6px" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >
                          Visit Website
                        </button>
                      )}
                      {templateButtons.filter(b => b.type === "QUICK_REPLY").length < 10 && (
                        <button
                          type="button"
                          onClick={() => {
                            setTemplateButtons([...templateButtons, { id: Date.now() + Math.random(), type: "QUICK_REPLY", text: "" }]);
                            setShowAddButtonDropdown(false);
                          }}
                          style={{ padding: "8px 12px", textAlign: "left", fontSize: "12px", fontWeight: 600, background: "none", border: "none", cursor: "pointer", borderRadius: "6px" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >
                          Quick Replies
                        </button>
                      )}
                      {templateButtons.filter(b => b.type === "PHONE_NUMBER").length === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setTemplateButtons([...templateButtons, { id: Date.now() + Math.random(), type: "PHONE_NUMBER", text: "", countryCode: "+1", phone: "" }]);
                            setShowAddButtonDropdown(false);
                          }}
                          style={{ padding: "8px 12px", textAlign: "left", fontSize: "12px", fontWeight: 600, background: "none", border: "none", cursor: "pointer", borderRadius: "6px" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >
                          Call Phone Number
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Language Selection Dropdown */}
        <div>
          <CustomDropdown
            label="Language"
            options={LANGUAGES}
            value={language}
            onChange={setLanguage}
            disabled={isEdit}
          />
        </div>

        {templateType === "CATALOG" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            
            {/* Catalogue Format Choice */}
            <div className="wa-section" style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "18px", borderRadius: "12px" }}>
              <label className="wa-label" style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b", marginBottom: "4px" }}>Catalogue format</label>
              <p style={{ fontSize: "12px", color: "#64748b", margin: "0 0 16px" }}>Choose the message format that best fits your needs.</p>
              
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="catalogFormat"
                    checked={catalogFormat === "CATALOG"}
                    onChange={() => setCatalogFormat("CATALOG")}
                    style={{ accentColor: "#18181b", width: "16px", height: "16px", marginTop: "3px" }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>Catalogue message</span>
                    <span style={{ fontSize: "11.5px", color: "#64748b" }}>Include the entire catalogue to give your users a comprehensive view of all of your products.</span>
                  </div>
                </label>

                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="catalogFormat"
                    checked={catalogFormat === "MULTI_PRODUCT"}
                    onChange={() => setCatalogFormat("MULTI_PRODUCT")}
                    style={{ accentColor: "#18181b", width: "16px", height: "16px", marginTop: "3px" }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>Multi-product message</span>
                    <span style={{ fontSize: "11.5px", color: "#64748b" }}>Include up to 30 products from the catalogue. Useful for showcasing new collection or a specific product category. You will need to specify which products via the API.</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Catalogue Setup Info */}
            <div className="wa-section" style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "18px", borderRadius: "12px" }}>
              <label className="wa-label" style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b", marginBottom: "4px" }}>Catalogue setup</label>
              
              {isCatalogConnected ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "10px 14px", borderRadius: "10px", marginBottom: "16px" }}>
                  <span style={{ color: "#16a34a", fontSize: "16px" }}>✓</span>
                  <span style={{ fontSize: "12px", color: "#15803d", fontWeight: 600 }}>
                    Connected: Catalogue_Products (1438875591459198)
                  </span>
                </div>
              ) : (
                <p style={{ fontSize: "12px", color: "#64748b", margin: "0 0 16px" }}>
            <button type="button" onClick={() => {}} style={{ background: "none", border: "none", color: "#18181b", fontWeight: 600, cursor: "pointer", padding: 0, fontSize: "inherit" }}>Learn more</button>
                </p>
              )}

              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "16px" }}>
                <Info size={16} className="text-zinc-500 shrink-0 mt-0.5" />
                <span style={{ fontSize: "12px", color: "#475569", fontWeight: "bold", lineHeight: 1.45 }}>
                  Please note that you cannot add media to this template. Instead, you can link your catalogue to showcase your products.
                </span>
              </div>

              <button
                type="button"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors bg-zinc-900 text-white hover:bg-zinc-800 cursor-pointer shadow-xs"
                onClick={() => setShowCatalogModal(true)}
              >
                {isCatalogConnected ? "Change catalogue" : "Connect catalogue"}
              </button>
            </div>

            {/* Non-editable Button Text */}
            <div className="wa-section" style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "18px", borderRadius: "12px" }}>
              <label className="wa-label" style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b", marginBottom: "4px" }}>Button</label>
              
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "16px" }}>
                <Info size={16} className="text-zinc-500 shrink-0 mt-0.5" />
                <span style={{ fontSize: "12px", color: "#475569", fontWeight: "bold", lineHeight: 1.45 }}>
                  Only one button is supported for this type of template. The button text is not editable.
                </span>
              </div>

              <div>
                <label className="wa-label" style={{ fontSize: "11px", fontWeight: "700", color: "#475569" }}>Button text</label>
                <input
                  type="text"
                  className="wa-input"
                  value={catalogFormat === "MULTI_PRODUCT" ? "View items" : "View catalog"}
                  disabled
                  style={{ background: "#f1f5f9", cursor: "not-allowed", color: "#64748b", fontWeight: 700 }}
                />
              </div>
            </div>

          </div>
        )}

        {/* Header (Only for default or catalog templates) */}
        {templateType !== "CALL_PERMISSION" && templateType !== "OTP" && templateType !== "CATALOG" && (
          <div>
            <label className="wa-label">Header (Optional)</label>
            <p style={{ fontSize: "11px", color: "#64748b", margin: "-4px 0 10px" }}>Add a title or select the media type you want to get approved for this template's header.</p>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              {["NONE", "TEXT", "IMAGE", "VIDEO", "DOCUMENT"].map(type => (
                <label key={type} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "#334155", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="headerType"
                    checked={headerType === type}
                    onChange={() => setHeaderType(type)}
                    style={{ accentColor: "#18181b", width: "16px", height: "16px" }}
                  />
                  {type.charAt(0) + type.slice(1).toLowerCase()}
                </label>
              ))}
            </div>

            {headerType === "TEXT" && (
              <div style={{ marginTop: "12px" }}>
                <input
                  className="wa-input"
                  placeholder="Enter header text (max 60 chars)"
                  maxLength={60}
                  value={headerText}
                  onChange={e => setHeaderText(e.target.value)}
                />
                {headerVars.length > 0 && (
                  <div style={{ marginTop: "8px", background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <label className="wa-label" style={{ fontSize: "11px" }}>Sample Header Value:</label>
                    <input
                      className="wa-input"
                      placeholder="e.g. #12345"
                      value={sampleHeader}
                      onChange={e => setSampleHeader(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {(headerType === "IMAGE" || headerType === "VIDEO" || headerType === "DOCUMENT") && (
              <div style={{ marginTop: "12px" }}>
                <label className="wa-label" style={{ fontSize: "11px" }}>Upload Sample Media File:</label>
                
                <input
                  type="file"
                  accept={headerType === "IMAGE" ? "image/png, image/jpeg" : headerType === "VIDEO" ? "video/mp4" : "application/pdf"}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    setLocalPreviewUrl(URL.createObjectURL(file));
                    setUploadedFileName(file.name);
                    
                    const formData = new FormData();
                    formData.append("file", file);
                    
                    showToast("Uploading sample file to Meta...", "info");
                    try {
                      const res = await ApiClient.request("/whatsapp/templates/upload-sample", {
                        method: "POST",
                        body: formData
                      });
                      if (res && res.handle) {
                        setSampleMediaUrl(res.handle);
                        showToast("Sample uploaded and verified by Meta!", "success");
                      }
                    } catch (err) {
                      showToast("Failed to upload sample media: " + (err.message || "Upload failed"), "error");
                    }
                  }}
                  style={{ display: "none" }}
                  id="sample-file-upload"
                />

                {!sampleMediaUrl ? (
                  <div 
                    style={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: "8px",
                      padding: "20px",
                      textAlign: "center",
                      background: "#f8fafc",
                      cursor: "pointer"
                    }}
                    onClick={() => document.getElementById("sample-file-upload").click()}
                  >
                    <div style={{ fontSize: "13px", color: "#475569", fontWeight: 500 }}>Drag and drop to upload</div>
                    <div style={{ fontSize: "13px", color: "#0284c7", fontWeight: 600, marginTop: "2px" }}>
                      Or <span style={{ textDecoration: "underline" }}>choose files on your device</span>
                    </div>
                  </div>
                ) : (
                  <div 
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "#e2e8f0",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      marginTop: "8px"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {localPreviewUrl && headerType === "IMAGE" && (
                        <img 
                          src={localPreviewUrl} 
                          alt="Thumbnail" 
                          style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "4px" }} 
                        />
                      )}
                      {localPreviewUrl && headerType === "VIDEO" && (
                        <video 
                          src={localPreviewUrl} 
                          style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "4px" }} 
                        />
                      )}
                      {headerType === "DOCUMENT" && (
                        <div style={{ width: "36px", height: "36px", background: "#cbd5e1", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px" }}>
                          <span style={{ fontSize: "10px", fontWeight: "bold" }}>PDF</span>
                        </div>
                      )}
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
                        {uploadedFileName || "sample_file"}
                      </span>
                    </div>
                    <button 
                      onClick={() => {
                        setSampleMediaUrl("");
                        setUploadedFileName("");
                        setLocalPreviewUrl("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#475569",
                        cursor: "pointer",
                        padding: "4px 8px"
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}

                <p style={{ fontSize: "10.5px", color: "#64748b", marginTop: "6px", lineHeight: 1.4 }}>
                  A sample media file is required by Meta for validation when submitting templates with media headers.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Body Text (Only for non-OTP templates) */}
        {templateType !== "OTP" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <label className="wa-label" style={{ margin: 0 }}>Body</label>
              <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 500 }}>{body.length}/1024</span>
            </div>
            <p style={{ fontSize: "11px", color: "#64748b", margin: "-4px 0 10px" }}>The Whatsapp message in the language you have selected</p>
            <textarea
              className="wa-input"
              rows={5}
              maxLength={1024}
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Type body text here... Use {{1}} for variables."
              style={{ resize: "vertical", minHeight: "120px" }}
            />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
              <span onClick={addVariable} style={{ color: "#18181b", fontSize: "13px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "2px" }}>
                + Add variable
              </span>
            </div>

            {/* Sample Variables */}
            {bodyVars.length > 0 && (
              <div style={{ marginTop: "16px", background: "#f8fafc", borderRadius: "12px", padding: "16px", border: "1px solid #e2e8f0" }}>
                <p style={{ fontSize: "12px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Define variables samples:</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {bodyVars.map(v => (
                    <div key={v} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{ fontFamily: "monospace", fontSize: "13px", fontWeight: 700, color: "#18181b", minWidth: "36px" }}>{`{{${v}}}`}</span>
                      <input
                        className="wa-input"
                        placeholder={`Sample value for {{${v}}}`}
                        value={sampleBody[v - 1] || ""}
                        onChange={e => {
                          const newSamples = [...sampleBody];
                          newSamples[v - 1] = e.target.value;
                          setSampleBody(newSamples);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* OTP Template Configs */}
        {templateType === "OTP" && (
          <div className="wa-section" style={{ background: "#f8fafc", display: "flex", flexDirection: "column", gap: "16px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>Authentication OTP Setup</span>
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "13px", fontWeight: 600, color: "#334155" }}>
              <input
                type="checkbox"
                checked={addSecurityRecommendation}
                onChange={e => setAddSecurityRecommendation(e.target.checked)}
                style={{ accentColor: "#18181b", width: "16px", height: "16px" }}
              />
              Add security recommendation text (e.g. "For security, do not share this code")
            </label>
            <div>
              <label className="wa-label">Code Expiration (minutes)</label>
              <input
                type="number"
                min={1}
                max={90}
                className="wa-input"
                value={codeExpirationMinutes}
                onChange={e => setCodeExpirationMinutes(parseInt(e.target.value) || 10)}
              />
            </div>
            <div>
              <label className="wa-label">Copy Code Button Label</label>
              <input
                className="wa-input"
                placeholder="e.g. Copy Code"
                value={copyCodeButton}
                onChange={e => setCopyCodeButton(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Footer (Only for default and catalog templates) */}
        {templateType !== "CALL_PERMISSION" && templateType !== "OTP" && (
          <div>
            <label className="wa-label">Footer (Optional)</label>
            <input
              className="wa-input"
              placeholder="Enter footer text (max 60 chars)"
              maxLength={60}
              value={footer}
              onChange={e => setFooter(e.target.value)}
            />
          </div>
        )}



        {/* Delete button if editing */}
        {isEdit && (
          <button
            onClick={() => setShowDeleteModal(true)}
            style={{
              display: "flex", alignItems: "center", gap: "6px", alignSelf: "flex-start",
              padding: "10px 18px", borderRadius: "12px", cursor: "pointer",
              background: "#fff", border: "1.5px solid #fecaca", color: "#ef4444",
              fontWeight: 700, fontSize: "13px"
            }}
          >
            <Trash2 size={14} /> Delete Template
          </button>
        )}
      </div>

      {/* Right Preview Panel */}
      <div style={{ width: "340px", position: "sticky", top: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#64748b" }}>Preview</span>
        </div>

        {/* Mock Phone Chat View */}
        <div style={{
          background: "#efeae2",
          backgroundImage: "radial-gradient(#e5ddd5 20%, transparent 20%), radial-gradient(#e5ddd5 20%, transparent 20%)",
          backgroundPosition: "0 0, 8px 8px",
          backgroundSize: "16px 16px",
          borderRadius: "24px", padding: "16px",
          border: "8px solid #0f172a", height: "460px", display: "flex", flexDirection: "column", justifyContent: "flex-start"
        }}>
          {/* Mock message bubble */}
          <div style={{
            background: "#fff", borderRadius: "14px", borderTopLeftRadius: 0,
            boxShadow: "0 1px 2px rgba(0,0,0,0.15)", padding: "10px 12px 0 12px",
            width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "4px"
          }}>
            {/* Header Preview */}
            {templateType !== "CALL_PERMISSION" && templateType !== "OTP" && headerType !== "NONE" && (
              <div style={{ marginBottom: "6px" }}>
                {headerType === "TEXT" && (
                  <p style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a", margin: 0 }}>
                    {getPreviewHeader() || "[Header Text]"}
                  </p>
                )}
                {headerType === "IMAGE" && (
                  <div style={{ width: "100%", height: "120px", background: "#f1f5f9", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {localPreviewUrl ? (
                      <img src={localPreviewUrl} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <ImageIcon size={28} color="#94a3b8" />
                    )}
                  </div>
                )}
                {headerType === "VIDEO" && (
                  <div style={{ width: "100%", height: "120px", background: "#f1f5f9", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {localPreviewUrl ? (
                      <video src={localPreviewUrl} controls autoPlay muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <Video size={28} color="#94a3b8" />
                    )}
                  </div>
                )}
                {headerType === "DOCUMENT" && (
                  <div style={{ width: "100%", height: "54px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", display: "flex", alignItems: "center", gap: "10px", padding: "0 12px" }}>
                    <FileText size={20} color="#94a3b8" />
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#475569" }}>document.pdf</span>
                  </div>
                )}
              </div>
            )}

            {templateType === "CATALOG" && catalogFormat === "CATALOG" && (
              <div style={{ width: "100%", height: "130px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", display: "flex", flexDirection: "column", gap: "6px", alignItems: "center", justifyContent: "center", overflow: "hidden", marginBottom: "8px" }}>
                <ShoppingBag size={36} color="#02987a" />

                <span style={{ fontSize: "11px", fontWeight: 700, color: "#475569" }}>Catalog</span>
              </div>
            )}

            {/* Body Preview */}
            <p style={{ fontSize: "13px", color: "#1e293b", margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              {getPreviewText() || "Message body preview..."}
            </p>

            {/* Footer Preview */}
            {templateType !== "CALL_PERMISSION" && footer.trim() && (
              <p style={{ fontSize: "11px", color: "#64748b", margin: "2px 0 0" }}>{footer}</p>
            )}

            {/* Timestamp Mock */}
            <span style={{ fontSize: "10px", color: "#94a3b8", alignSelf: "flex-end", marginTop: "2px", marginBottom: "8px" }}>10:04</span>

            {/* Render Buttons Mock directly attached with borders */}
            {templateType === "CATALOG" && (
              <div style={{ borderTop: "1px solid #e2e8f0", margin: "0 -12px", display: "flex", flexDirection: "column" }}>
                <div style={{
                  padding: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  fontSize: "13px", fontWeight: 700, color: "#02987a", cursor: "pointer"
                }}>
                  <ShoppingBag size={15} />
                  <span>{catalogFormat === "MULTI_PRODUCT" ? "View items" : "View catalog"}</span>
                </div>
              </div>
            )}

            {templateType === "CALL_PERMISSION" && (
              <div style={{ borderTop: "1px solid #e2e8f0", margin: "0 -12px", display: "flex", flexDirection: "column" }}>
                <div style={{
                  padding: "12px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  fontSize: "13px", fontWeight: 700, color: "#008a5e", cursor: "pointer"
                }}>
                  <PhoneCall size={15} />
                  <span>Share permission</span>
                </div>
                <div style={{
                  padding: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  fontSize: "13px", fontWeight: 700, color: "#ef4444", cursor: "pointer"
                }}>
                  <span>Cancel</span>
                </div>
              </div>
            )}

            {templateType === "DEFAULT" && templateButtons.length > 0 && (
              <div style={{ borderTop: "1px solid #e2e8f0", margin: "0 -12px", display: "flex", flexDirection: "column" }}>
                {templateButtons.map((btn, i) => (
                  <div key={i} style={{
                    padding: "12px", borderBottom: i < templateButtons.length - 1 ? "1px solid #e2e8f0" : "none",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    fontSize: "13px", fontWeight: 700, color: "#02987a", cursor: "pointer"
                  }}>
                    {btn.type === "URL" && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                    )}
                    {btn.type === "QUICK_REPLY" && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>
                    )}
                    {btn.type === "COPY_CODE" && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                    )}
                    {btn.type === "PHONE_NUMBER" && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                    )}
                    <span>{btn.type === "COPY_CODE" ? "Copy Code" : (btn.text || (btn.type === "URL" ? "Visit Website" : btn.type === "PHONE_NUMBER" ? "Call phone number" : "Quick Reply"))}</span>
                  </div>
                ))}
              </div>
            )}

            {templateType === "OTP" && (
              <div style={{ borderTop: "1px solid #e2e8f0", margin: "0 -12px", display: "flex", flexDirection: "column" }}>
                <div style={{
                  padding: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  fontSize: "13px", fontWeight: 700, color: "#008a5e", cursor: "pointer"
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  <span>Copy Code: {copyCodeButton || "Code"}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Connect a Catalogue Modal */}
      {showCatalogModal && (
        <CatalogModal
          onClose={() => { setShowCatalogModal(false); setCatalogSearch(""); }}
          onConnect={(cat) => {
            setIsCatalogConnected(true);
            setShowCatalogModal(false);
            setCatalogSearch("");
            showToast(`Connected: ${cat.name}`, "success");
          }}
          isCatalogConnected={isCatalogConnected}
          catalogSearch={catalogSearch}
          setCatalogSearch={setCatalogSearch}
          showToast={showToast}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Template"
        message={`Delete "${name}"? The template name cannot be reused for 30 days.`}
        confirmText="Delete Template"
      />

      {/* Floating Save Actions block */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-zinc-200 px-6 py-3.5 flex justify-end gap-2.5 z-50">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-xs font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 active:scale-[0.98] cursor-pointer shadow-xs"
        >
          {saving && <Loader2 size={13} className="animate-spin" />}
          <span>{saving ? "Submitting..." : isEdit ? "Save Changes" : "Submit Template"}</span>
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const WhatsAppTemplates = ({ activeAiAgentId }) => {
  const navigate = useNavigate();


  const [view, setView] = useState("list");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    const closeMenu = () => setActiveActionMenu(null);
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, []);

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    try {
      await ApiClient.delete(`/whatsapp/templates?name=${encodeURIComponent(templateToDelete.name)}`);
      showToast("Template deleted successfully", "success");
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (e) {
      showToast(e.message || "Failed to delete template", "error");
    } finally {
      setDeleting(false);
    }
  };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiClient.request("/whatsapp/templates", { aiAgentId: activeAiAgentId });
      setTemplates(res.templates || []);
    } catch (e) {
      // Handled globally
    } finally {
      setLoading(false);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Main UI Filtering
  const filteredTemplates = templates.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreateSuccess = () => {
    setView("list");
    fetchTemplates();
  };

  const getLanguageLabel = (code) => {
    return LANGUAGES.find(l => l.value === code)?.label || code;
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif]">
      <style>{`
        .wa-input { width: 100%; padding: 8px 12px; background: #fafafa; border: 1px solid #e4e4e7; border-radius: 8px; font-size: 12px; color: #18181b; outline: none; transition: all 0.15s; box-sizing: border-box; font-family: inherit; }
        .wa-input:focus { border-color: #18181b; background: #fff; }
        .wa-label { display: block; font-size: 12px; font-weight: 600; color: #18181b; margin-bottom: 4px; }
        .wa-section { background: #fff; border: 1px solid #e4e4e7; border-radius: 12px; padding: 20px 24px; margin-bottom: 16px; }
        .wa-section-title { font-size: 12px; font-weight: 700; color: #18181b; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; text-transform: uppercase; letter-spacing: 0.05em; }
        .wa-btn-outline { padding: 6px 12px; border: 1px solid #e4e4e7; border-radius: 8px; background: #fff; cursor: pointer; font-size: 12px; font-weight: 600; color: #3f3f46; transition: all 0.15s; }
        .wa-btn-outline:hover { background: #f4f4f5; border-color: #d4d4d8; color: #18181b; }
        .wa-filter-btn { padding: 5px 12px; border-radius: 6px; border: 1px solid #e4e4e7; background: #fff; cursor: pointer; font-size: 11px; font-weight: 600; color: #71717a; transition: all 0.15s; }
        .wa-filter-btn.active { background: #18181b; color: #fff; border-color: #18181b; }
        .wa-filter-btn:hover:not(.active) { border-color: #d4d4d8; background: #f4f4f5; color: #18181b; }
      `}</style>

      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">

        {/* Main List View */}
        {view === "list" && (
          <div className="w-full space-y-5">
            {/* Top Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate("/campaigns", { state: { openWhatsAppCampaign: true } })}
                  className="w-8 h-8 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer flex-shrink-0"
                  title="Back to Campaigns"
                >
                  <ArrowLeft size={15} />
                </button>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">WhatsApp Templates</h1>
                  <p className="text-xs text-zinc-500 mt-0.5">Manage and synchronize Meta-approved WhatsApp message templates.</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setView("create")}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors bg-zinc-900 text-white hover:bg-zinc-800 self-start sm:self-auto cursor-pointer shadow-xs active:scale-[0.98]"
              >
                <Plus size={14} />
                <span>Create Template</span>
              </button>
            </div>

            {/* Search bar */}
            <div className="relative">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                placeholder="Search templates by name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 hover:bg-zinc-50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 placeholder:text-zinc-400 transition-colors shadow-2xs"
              />
            </div>

            {/* Table or Loading */}
            {loading ? (
              <div className="space-y-2 py-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 rounded-lg bg-zinc-100/70 animate-pulse" />
                ))}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-16 bg-zinc-50/50 rounded-xl border border-dashed border-zinc-200">
                <MessageSquare size={36} className="text-zinc-300 mx-auto mb-2" />
                <p className="text-xs font-semibold text-zinc-800">No templates found</p>
                <p className="text-xs text-zinc-500 mt-0.5">Create a WhatsApp template to start broadcasting campaigns.</p>
              </div>
            ) : (
              <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white shadow-2xs">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                        <th className="px-4 py-3">Template Name</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Category</th>
                        <th className="px-4 py-3">Language</th>
                        <th className="px-4 py-3">Last Edited</th>
                        <th className="px-4 py-3">Created By</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {filteredTemplates.map(t => {
                        const statusCfg = STATUS_CONFIG[t.status] || STATUS_CONFIG["PENDING"];
                        return (
                          <tr
                            key={t.id || t.name}
                            onClick={() => { setSelectedTemplate(t); setView("edit"); }}
                            className="hover:bg-zinc-50/70 transition-colors cursor-pointer group"
                          >
                            <td className="px-4 py-3 text-xs font-semibold text-zinc-900 flex items-center gap-2">
                              <MessageSquare size={14} className="text-zinc-500 group-hover:text-zinc-900 shrink-0 transition-colors" />
                              <span>{t.name}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${statusCfg.className || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>
                                {statusCfg.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-zinc-600 font-medium">
                              {t.category ? t.category.charAt(0) + t.category.slice(1).toLowerCase() : "—"}
                            </td>
                            <td className="px-4 py-3 text-xs text-zinc-600 font-medium">
                              {getLanguageLabel(t.language)}
                            </td>
                            <td className="px-4 py-3 text-xs text-zinc-500">
                              {(() => {
                                if (!t.last_updated_time) return "—";
                                try {
                                  const num = Number(t.last_updated_time);
                                  if (!isNaN(num)) {
                                    return new Date(num * 1000).toLocaleString();
                                  }
                                  return new Date(t.last_updated_time).toLocaleString();
                                } catch (e) {
                                  return t.last_updated_time;
                                }
                              })()}
                            </td>
                            <td className="px-4 py-3 text-xs text-zinc-500">
                              Internal Admin
                            </td>
                            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                              <div className="relative inline-block text-left">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveActionMenu(activeActionMenu === t.id ? null : t.id);
                                  }}
                                  className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors cursor-pointer"
                                >
                                  <MoreVertical size={14} />
                                </button>
                                {activeActionMenu === t.id && (
                                  <div className="absolute right-0 top-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 z-50 min-w-[100px]">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setTemplateToDelete(t);
                                        setShowDeleteConfirm(true);
                                        setActiveActionMenu(null);
                                      }}
                                      className="w-full px-3 py-1.5 text-xs text-left font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create / Edit View */}
        {view !== "list" && (
          <div className="w-full space-y-4 pb-24">
            <div className="flex items-center justify-between pb-4 border-b border-zinc-100">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setView("list"); setSelectedTemplate(null); }}
                  className="w-8 h-8 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer flex-shrink-0"
                  title="Back to Templates"
                >
                  <ArrowLeft size={15} />
                </button>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                    {view === "create" ? "Create New Template" : selectedTemplate?.name}
                  </h1>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {view === "create" ? "Build and submit a new WhatsApp message template for Meta approval." : "Review and update template configuration."}
                  </p>
                </div>
              </div>
            </div>

            <TemplateForm
              isEdit={view === "edit"}
              templateId={selectedTemplate?.id}
              initial={view === "edit" ? parseExistingTemplate(selectedTemplate) : emptyForm}
              rejectedReason={selectedTemplate?.rejected_reason}
              status={selectedTemplate?.status}
              onSave={handleCreateSuccess}
              onCancel={() => { setView("list"); setSelectedTemplate(null); }}
              onDelete={() => { setView("list"); setSelectedTemplate(null); fetchTemplates(); }}
            />
          </div>
        )}

        {/* Delete Confirmation Modal for List View */}
        <DeleteConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => { setShowDeleteConfirm(false); setTemplateToDelete(null); }}
          onConfirm={handleDeleteTemplate}
          title="Delete Template"
          message={templateToDelete ? `Delete "${templateToDelete.name}"? This template will be permanently removed from your WhatsApp Business Account. Name cannot be reused for 30 days.` : ""}
          confirmText={deleting ? "Deleting..." : "Delete Template"}
        />
      </div>
    </div>
  );
};

export default WhatsAppTemplates;
